import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { addMedicineApi, updateMedicineApi, deleteMedicineApi } from "../services/apiService";
import { useMedicationList } from "../queries/usePrescriptionsQuery";
import editIcon from "../assets/images/edit-icon.svg";
import deleteIcon from "../assets/images/delete-icon.svg";
import cancelIcon from "../assets/images/cancel-btn.svg";
import { formatDateInDayDateYear } from "../utils/commonData";

const getMedicationRows = response => {
  if (response?.data?.res === true && Array.isArray(response.data.list)) {
    return response.data.list;
  }

  if (Array.isArray(response?.data?.data?.list)) {
    return response.data.data.list;
  }

  return [];
};

const normalizeMedication = item => ({
  ...item,
  name: item?.name || item?.brand || item?.denomination || "",
  dosage: item?.dosage || "",
  notes: item?.notes || "",
});

const filterMedications = (medicines, searchValue) => {
  const query = searchValue.trim().toLowerCase();
  if (!query) return medicines;

  return medicines.filter(item =>
    [
      item?.name,
      item?.brand,
      item?.denomination,
      item?.forms,
      item?.form,
      item?.dosage,
      item?.conditionnement,
      item?.packaging,
      item?.notes,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query)
  );
};

const PAGINATION_LIMIT_OPTIONS = [10, 20, 30, 40, 50];

const getPaginationItems = (totalPages, currentPage) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  let firstAdjacentPage = Math.max(2, currentPage - 1);
  let lastAdjacentPage = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 4) lastAdjacentPage = 5;
  if (currentPage >= totalPages - 3) firstAdjacentPage = totalPages - 4;

  const items = [1];
  if (firstAdjacentPage > 2) items.push("start-ellipsis");

  for (let page = firstAdjacentPage; page <= lastAdjacentPage; page += 1) {
    items.push(page);
  }

  if (lastAdjacentPage < totalPages - 1) items.push("end-ellipsis");
  items.push(totalPages);
  return items;
};

const MedicamentManagement = () => {
  const [filteredMedicineList, setFilteredMedicineList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMedicineModal, setShowMedicineModal] = useState(false);
  const [showDeleteMedicinePopup, setShowDeleteMedicinePopup] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [paginationPage, setPaginationPage] = useState(1);
  const [paginationOffset, setPaginationOffset] = useState(0);
  const [paginationLimit, setPaginationLimit] = useState(10);
  const [medicineFormObj, setMedicineFormObj] = useState({ 
    id: "",
    name: "", 
    dosage: "", 
    notes: ""
  });
  const [addMedBtnDis, setAddMedBtnDis] = useState(false);
  const [updateMedBtnDis, setUpdateMedBtnDis] = useState(false);
  const [deleteMedBtnDis, setDeleteMedBtnDis] = useState(false);
  const {
    data: medicationsResponse,
    isError: isMedicationListError,
    refetch: refetchMedicationList,
  } = useMedicationList();

  const totalPages = Math.max(1, Math.ceil(filteredMedicineList.length / paginationLimit));
  const paginatedMedicineList = filteredMedicineList.slice(
    paginationOffset,
    paginationOffset + paginationLimit
  );
  const paginationItems = getPaginationItems(totalPages, paginationPage);
  const displayedStart = filteredMedicineList.length > 0 ? paginationOffset + 1 : 0;
  const displayedEnd = Math.min(paginationOffset + paginationLimit, filteredMedicineList.length);

  const handlePaginationPageChange = page => {
    if (page < 1 || page > totalPages || page === paginationPage) return;

    setPaginationPage(page);
    setPaginationOffset((page - 1) * paginationLimit);
  };

  const handlePaginationLimitChange = event => {
    const nextLimit = Number(event.target.value);
    if (!PAGINATION_LIMIT_OPTIONS.includes(nextLimit)) return;

    setPaginationLimit(nextLimit);
    setPaginationPage(1);
    setPaginationOffset(0);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPaginationPage(1);
    setPaginationOffset(0);
  };

  useEffect(() => {
    const medicines = getMedicationRows(medicationsResponse).map(normalizeMedication);
    setFilteredMedicineList(filterMedications(medicines, searchTerm));
    setPaginationPage(1);
    setPaginationOffset(0);
  }, [medicationsResponse, searchTerm]);

  useEffect(() => {
    if (isMedicationListError) {
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    }
  }, [isMedicationListError]);

  const handleAddMedicine = async () => {
    if (!medicineFormObj?.name) {
      toast.warning("Please add medicine name.", { autoClose: 1500 });
    } else if (!medicineFormObj?.dosage) {
      toast.warning("Please add dosage.", { autoClose: 1500 });
    } else {
      try {
        setAddMedBtnDis(true);
        let formData = new FormData();
        formData.append("name", medicineFormObj?.name);
        formData.append("dosage", medicineFormObj?.dosage);
        formData.append("notes", medicineFormObj?.notes || "");
        let response = await addMedicineApi(formData);
        if (response) {
          toast.success("Medicine added successfully.", { autoClose: 1500 });
          setMedicineFormObj({ id: "", name: "", dosage: "", notes: "" });
          setShowMedicineModal(false);
          refetchMedicationList();
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setAddMedBtnDis(false);
      }
    }
  };

  const handleUpdateMedicine = async () => {
    if (!medicineFormObj?.id) {
      toast.warning("Medicine is not available.", { autoClose: 1500 });
    } else if (!medicineFormObj?.name) {
      toast.warning("Please add medicine name.", { autoClose: 1500 });
    } else if (!medicineFormObj?.dosage) {
      toast.warning("Please add dosage.", { autoClose: 1500 });
    } else {
      try {
        setUpdateMedBtnDis(true);
        let formData = new FormData();
        formData.append("id", medicineFormObj?.id);
        formData.append("name", medicineFormObj?.name);
        formData.append("dosage", medicineFormObj?.dosage);
        formData.append("notes", medicineFormObj?.notes || "");
        let response = await updateMedicineApi(formData);
        if (response) {
          toast.success("Medicine updated successfully.", { autoClose: 1500 });
          setMedicineFormObj({ id: "", name: "", dosage: "", notes: "" });
          setShowMedicineModal(false);
          refetchMedicationList();
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setUpdateMedBtnDis(false);
      }
    }
  };

  const handleDeleteMedicine = async () => {
    if (!selectedMedicine?.id) {
      toast.warning("Medicine is not available.", { autoClose: 1500 });
    } else {
      try {
        setDeleteMedBtnDis(true);
        let formData = new FormData();
        formData.append("id", selectedMedicine?.id);
        let response = await deleteMedicineApi(formData);
        if (response) {
          toast.success("Medicine removed successfully.", { autoClose: 1500 });
          setSelectedMedicine(null);
          setShowDeleteMedicinePopup(false);
          refetchMedicationList();
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        setDeleteMedBtnDis(false);
      }
    }
  };

  return (
    <>
      <div className="configuration_prescription_management mainProtocolSection">
        <h3 style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "15px" }}>
          Medicament Management
          <span style={{ display: "flex", gap: "12px", alignItems: "center", position: "relative", flex: "0 1 auto", minWidth: 0 }}>
            <div style={{ position: "relative", flex: "0 1 350px", minWidth: "150px" }}>
               <input
                type="text"
                placeholder="Search medicines..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                autoComplete="off"
                style={{
                  padding: "8px 12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  fontSize: "14px",
                  width: "100%",
                  boxSizing: "border-box"
                }}
              />
            </div>
            <button
              className="createNewBtn"
              onClick={() => {
                setShowMedicineModal(true);
                setSelectedMedicine(null);
                setMedicineFormObj({ id: "", name: "", dosage: "", notes: "" });
              }}>
              Add Medicine
            </button>
          </span>
        </h3>
        <div className="upcomingAppoinment nurseDiv protocolSection">
          <div className="nurseList">
            <div className="appoList">
              <table>
                <thead>
                  <tr>
                    <th>Medicine Name</th>
                    <th>Dosage</th>
                    <th>Notes</th>
                    <th>Created on</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                {filteredMedicineList?.length > 0 ? (
                  <tbody>
                    {paginatedMedicineList.map((item, index) => (
                      <tr key={item?.id ?? `${paginationOffset}-${index}`}>
                        <td className="appoName">{item?.name}</td>
                        <td>{item?.dosage || 'N/A'}</td>
                        <td>{item?.notes || 'N/A'}</td>
                        <td>{formatDateInDayDateYear(item?.created_at)}</td>
                        <td>
                          <button
                            className="viewLink"
                            onClick={() => {
                              setSelectedMedicine(item);
                              setMedicineFormObj({
                                id: item?.id,
                                name: item?.name,
                                dosage: item?.dosage || "",
                                notes: item?.notes || ""
                              });
                              setShowMedicineModal(true);
                            }}>
                            <img src={editIcon} alt="editIcon" />
                          </button>{" "}
                          <button
                            className="viewLink"
                            onClick={() => {
                              setSelectedMedicine(item);
                              setShowDeleteMedicinePopup(true);
                            }}>
                            <img src={deleteIcon} alt="deleteIcon" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan="5" className="centeredText">
                        {searchTerm ? "No medicines found matching your search!" : "No Medicines found!"}
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
              {filteredMedicineList.length > 0 && (
                <nav
                  aria-label="Medication pagination"
                  style={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "16px",
                    justifyContent: "space-between",
                    padding: "18px 0 4px"
                  }}>
                  <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    <label htmlFor="medications-per-page" style={{ color: "#555", fontSize: "14px" }}>
                      Per page
                    </label>
                    <select
                      id="medications-per-page"
                      aria-label="Medications per page"
                      value={paginationLimit}
                      onChange={handlePaginationLimitChange}
                      style={{
                        backgroundColor: "#fff",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        padding: "7px 10px"
                      }}>
                      {PAGINATION_LIMIT_OPTIONS.map(limit => (
                        <option key={limit} value={limit}>{limit}</option>
                      ))}
                    </select>
                    <span style={{ color: "#777", fontSize: "14px" }}>
                      {displayedStart}-{displayedEnd} of {filteredMedicineList.length}
                    </span>
                  </div>

                  <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    <button
                      type="button"
                      aria-label="Previous page"
                      disabled={paginationPage === 1}
                      onClick={() => handlePaginationPageChange(paginationPage - 1)}
                      style={{
                        backgroundColor: "#fff",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        cursor: paginationPage === 1 ? "not-allowed" : "pointer",
                        opacity: paginationPage === 1 ? 0.5 : 1,
                        padding: "7px 12px"
                      }}>
                      Previous
                    </button>

                    {paginationItems.map(item => (
                      typeof item === "number" ? (
                        <button
                          key={item}
                          type="button"
                          aria-label={`Go to medication page ${item}`}
                          aria-current={item === paginationPage ? "page" : undefined}
                          onClick={() => handlePaginationPageChange(item)}
                          style={{
                            backgroundColor: item === paginationPage ? "#687eff" : "#fff",
                            border: `1px solid ${item === paginationPage ? "#687eff" : "#d9d9d9"}`,
                            borderRadius: "6px",
                            color: item === paginationPage ? "#fff" : "#333",
                            cursor: "pointer",
                            minWidth: "36px",
                            padding: "7px 9px"
                          }}>
                          {item}
                        </button>
                      ) : (
                        <span key={item} aria-hidden="true" style={{ color: "#777", padding: "0 3px" }}>
                          …
                        </span>
                      )
                    ))}

                    <button
                      type="button"
                      aria-label="Next page"
                      disabled={paginationPage === totalPages}
                      onClick={() => handlePaginationPageChange(paginationPage + 1)}
                      style={{
                        backgroundColor: "#fff",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        cursor: paginationPage === totalPages ? "not-allowed" : "pointer",
                        opacity: paginationPage === totalPages ? 0.5 : 1,
                        padding: "7px 12px"
                      }}>
                      Next
                    </button>
                  </div>
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>

      {showMedicineModal && (
        <div className="modal TemplateModal" onClick={() => setShowMedicineModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: "550px", borderRadius: "12px"}}>
            <button 
              className="CloseButton" 
              onClick={() => setShowMedicineModal(false)}
              style={{
                background: "none",
                backgroundImage: "url(/src/assets/images/modal-close-icon.svg)",
                backgroundPosition: "right center",
                backgroundRepeat: "no-repeat",
                border: "none",
                cursor: "pointer",
                fontSize: "20px",
                height: "29px",
                position: "absolute",
                right: "15px",
                top: "15px",
                width: "29px",
                transition: "transform 0.3s ease"
              }}
              onMouseEnter={e => e.target.style.transform = "scale(1.1)"}
              onMouseLeave={e => e.target.style.transform = "scale(1)"}
            ></button>
            <div className="modal-header" style={{ 
              borderBottom: "1px solid #e8e8e8", 
              padding: "20px 24px"
            }}>
              <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 600, color: "#1a1a1a" }}>
                {selectedMedicine ? "Edit Medicine" : "Add New Medicine"}
              </h3>
            </div>
            <div className="modal-body" style={{ padding: "24px" }}>
              <div className="white_form_box">
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  <div>
                    <label style={{ 
                      display: "block", 
                      marginBottom: "8px", 
                      fontSize: "14px", 
                      fontWeight: 500,
                      color: "#333"
                    }}>
                      Medicine Name <span style={{ color: "#ff4d4f" }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter medicine name"
                      className="form_control"
                      value={medicineFormObj?.name}
                      onChange={e => setMedicineFormObj(prev => ({ ...prev, name: e.target.value }))}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        fontSize: "14px",
                        transition: "all 0.3s",
                        outline: "none"
                      }}
                      onFocus={e => e.target.style.borderColor = "#7c3aed"}
                      onBlur={e => e.target.style.borderColor = "#d9d9d9"}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: "block", 
                      marginBottom: "8px", 
                      fontSize: "14px", 
                      fontWeight: 500,
                      color: "#333"
                    }}>
                      Dosage <span style={{ color: "#ff4d4f" }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter dosage"
                      className="form_control"
                      value={medicineFormObj?.dosage}
                      onChange={e => setMedicineFormObj(prev => ({ ...prev, dosage: e.target.value }))}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        fontSize: "14px",
                        transition: "all 0.3s",
                        outline: "none"
                      }}
                      onFocus={e => e.target.style.borderColor = "#7c3aed"}
                      onBlur={e => e.target.style.borderColor = "#d9d9d9"}
                    />
                  </div>
                  <div>
                    <label style={{ 
                      display: "block", 
                      marginBottom: "8px", 
                      fontSize: "14px", 
                      fontWeight: 500,
                      color: "#333"
                    }}>
                      Notes
                    </label>
                    <textarea
                      placeholder="Enter notes (optional)"
                      className="form_control"
                      value={medicineFormObj?.notes}
                      onChange={e => setMedicineFormObj(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        fontSize: "14px",
                        transition: "all 0.3s",
                        outline: "none",
                        resize: "vertical"
                      }}
                      onFocus={e => e.target.style.borderColor = "#7c3aed"}
                      onBlur={e => e.target.style.borderColor = "#d9d9d9"}
                    />
                  </div>
                </div>
                <div style={{ 
                  marginTop: "32px", 
                  display: "flex", 
                  gap: "12px", 
                  justifyContent: "flex-end",
                  paddingTop: "20px",
                  borderTop: "1px solid #f0f0f0"
                }}>
                  {selectedMedicine ? (
                    <button 
                      className="saveBtn" 
                      disabled={updateMedBtnDis} 
                      onClick={() => handleUpdateMedicine()}
                      style={{
                        width: "120px",
                        height: "53px",
                        background: updateMedBtnDis ? "#d9d9d9" : "#687eff",
                        fontSize: "16px",
                        fontWeight: 400,
                        fontFamily: '"Rubik", sans-serif',
                        borderRadius: "30px",
                        color: "#fff",
                        border: "none",
                        marginBottom: "20px",
                        cursor: updateMedBtnDis ? "not-allowed" : "pointer",
                        transition: "background 0.3s ease"
                      }}
                      onMouseEnter={e => !updateMedBtnDis && (e.target.style.background = "#5568e6")}
                      onMouseLeave={e => !updateMedBtnDis && (e.target.style.background = "#687eff")}
                    >
                      {updateMedBtnDis ? "Updating..." : "Update Medicine"}
                    </button>
                  ) : (
                    <button 
                      className="saveBtn" 
                      disabled={addMedBtnDis} 
                      onClick={() => handleAddMedicine()}
                      style={{
                        width: "120px",
                        height: "53px",
                        background: addMedBtnDis ? "#d9d9d9" : "#687eff",
                        fontSize: "16px",
                        fontWeight: 400,
                        fontFamily: '"Rubik", sans-serif',
                        borderRadius: "30px",
                        color: "#fff",
                        border: "none",
                        marginBottom: "20px",
                        cursor: addMedBtnDis ? "not-allowed" : "pointer",
                        transition: "background 0.3s ease"
                      }}
                      onMouseEnter={e => !addMedBtnDis && (e.target.style.background = "#5568e6")}
                      onMouseLeave={e => !addMedBtnDis && (e.target.style.background = "#687eff")}
                    >
                      {addMedBtnDis ? "Adding..." : "Add Medicine"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteMedicinePopup && (
        <div className="popup holidayPopup">
          <div className="popup-content removePop">
            <button className="cancelBtn" onClick={() => setShowDeleteMedicinePopup(false)}>
              <img src={cancelIcon} alt="cancelIcon" />
            </button>
            <div className="addNursePopup">
              <h2 className="removeHdn">Are you sure you want to delete this medicine?</h2>
              <div className="removeNurse">
                <div className="nurseBtns">
                  <button className="yesBtn" onClick={() => handleDeleteMedicine()} disabled={deleteMedBtnDis}>
                    {deleteMedBtnDis ? "Please wait..." : "Yes"}
                  </button>
                  <button className="noBtn" onClick={() => setShowDeleteMedicinePopup(false)}>
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MedicamentManagement;
